import { ComponentRendering } from '@sitecore-jss/sitecore-jss-react';
import {
  TProductDownloadBrochuresBackgroundImage,
  TProductDownloadBrochuresDataAssets,
  TProductDownloadBrochuresFields,
  TProductDownloadBrochuresProps,
} from '../types/productDownloadBrochures.types';
import { StringField } from '../../../../types/types';
import { appendRootToRichTextInternalLinks } from '../../../../utils/appendRootToRichTextInternalLinks.utils';
import { TSitecoreContext } from '../../../../types/sitecore.types';
import { TFunction } from 'i18next';
import { WithTranslation } from 'react-i18next';

export class TProductDownloadBrochureModel {
  /* eslint-disable-next-line */
  private readonly fields: TProductDownloadBrochuresFields;
  private readonly rendering?: ComponentRendering;
  private readonly shouldAppendRootToSubtitleLinks?: boolean;
  private readonly context?: TSitecoreContext;
  private readonly eyebrowDictionaryKeyBrochure?: string;
  private readonly titleDictionaryKeyBrochure?: string;
  private readonly descriptionDictionaryKeyBrochure?: string;
  private readonly t: TFunction;

  constructor(props: TProductDownloadBrochuresProps & WithTranslation) {
    this.fields = props.fields || null;
    this.rendering = props?.rendering;
    this.shouldAppendRootToSubtitleLinks = props.shouldAppendRootToSubtitleLinks;
    this.context = props?.sitecoreContext;
    this.eyebrowDictionaryKeyBrochure = props.eyebrowDictionaryKeyBrochure;
    this.titleDictionaryKeyBrochure = props.titleDictionaryKeyBrochure;
    this.descriptionDictionaryKeyBrochure = props.descriptionDictionaryKeyBrochure;
    this.t = props.t;
  }

  get thumbnailEE(): string {
    return this.fields?.itemData?.thumbnail || '';
  }

  get componentName(): string {
    return this.rendering?.componentName || '';
  }

  get title(): StringField {
    return { value: this.fields?.apiData?.brochures?.title };
  }

  get eyebrow(): StringField {
    return { value: this.fields?.apiData?.brochures?.eyebrow };
  }

  get description(): StringField {
    const descriptionValue =
      this.shouldAppendRootToSubtitleLinks !== false
        ? appendRootToRichTextInternalLinks(this.fields?.apiData?.brochures?.description)
        : this.fields?.apiData?.brochures?.description;
    return { value: descriptionValue || null };
  }

  get downloadBrochureCard(): TProductDownloadBrochuresDataAssets[] {
    return (this.fields?.apiData?.brochures?.assets || []).map((item) => {
      return {
        ...item,
        icon: this.fields?.itemData?.iconsList || [],
      };
    });
  }

  get thumbnail(): string {
    return this.fields?.apiData?.assets?.[0]?.thumbnail || '';
  }

  get canShowComponent(): boolean {
    return Boolean(
      this.fields?.apiData?.brochures?.title ||
        this.fields?.apiData?.brochures?.description ||
        this.fields?.apiData?.brochures?.eyebrow ||
        this.fields?.apiData?.brochures?.assets?.length > 0
    );
  }

  get desktopImage(): TProductDownloadBrochuresBackgroundImage | undefined {
    return this.fields?.itemData?.fields?.imageParameters?.desktopImageData;
  }

  get mobileImage(): TProductDownloadBrochuresBackgroundImage | undefined {
    return this.fields?.itemData?.fields?.imageParameters?.mobileImageData;
  }

  get defaultBgDesktopImage(): TProductDownloadBrochuresBackgroundImage | undefined {
    return {
      url: this.context?.defaultBackgroundImageParameters?.desktopImageData?.downloadBrochures,
    };
  }

  get defaultBgMobileImage(): TProductDownloadBrochuresBackgroundImage | undefined {
    return {
      url: this.context?.defaultBackgroundImageParameters?.mobileImageData?.downloadBrochures,
    };
  }

  get desktopBgImage(): TProductDownloadBrochuresBackgroundImage | undefined {
    return this.desktopImage?.url ? this.desktopImage : this.defaultBgDesktopImage;
  }

  get mobileBgImage(): TProductDownloadBrochuresBackgroundImage | undefined {
    return this.mobileImage?.url ? this.mobileImage : this.defaultBgMobileImage;
  }

  get contextItemUrl(): string {
    return this.fields?.itemData?.contextItemUrl || '';
  }

  get isUnreleased(): boolean {
    return !!this.fields?.isComponentUnreleased;
  }

  get showHeader(): boolean {
    const titleDictionaryValue = this.t(this.titleDictionaryKeyBrochure || '', {
      defaultValue: '',
    })?.trim();
    const descriptionDictionaryValue = this.t(this.descriptionDictionaryKeyBrochure || '', {
      defaultValue: '',
    })?.trim();
    const eyebrowDictionaryValue = this.t(this.eyebrowDictionaryKeyBrochure || '', {
      defaultValue: '',
    })?.trim();

    return Boolean(
      this.eyebrow?.value ||
        this.title?.value ||
        this.description?.value ||
        descriptionDictionaryValue ||
        titleDictionaryValue ||
        eyebrowDictionaryValue
    );
  }

  get showHeaderPicture(): boolean {
    return Boolean(this.eyebrow?.value || this.title?.value || this.description?.value);
  }
}
